import React, { useState, useMemo } from 'react';
import { useStore } from '../../store/useStore';
import { getCompendiumData, type CompendiumItem } from './compendium/CompendiumData';
import { CompendiumGrid } from './compendium/CompendiumGrid';
import { ItemDetailView } from './compendium/ItemDetailView';
import type { Resource } from '../../types';
import { Filter, BookOpen } from 'lucide-react';
import { SearchInput } from '../common/SearchInput';
import { ItemIcon } from '../common/ItemIcon';

export const CompendiumPanel: React.FC = () => {
    const resources = useStore((state) => state.resources);
    const market = useStore((state) => state.market);

    // State
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [selectedItem, setSelectedItem] = useState<CompendiumItem | null>(null);

    // Scroll Element State
    const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);

    // Find the global scroll container (main)
    React.useEffect(() => {
        const main = document.querySelector('main');
        setScrollElement(main as HTMLElement);
    }, []);

    // Data - Memoized to avoid rebuilding
    const allItems = useMemo(() => getCompendiumData(), []);

    // Helper for navigation from Detail View
    const handleNavigate = (itemId: Resource) => {
        const item = allItems.find(i => i.id === itemId);
        if (item) {
            setSelectedItem(item);
        }
    };

    // Filter Logic
    const filteredItems = useMemo(() => {
        let items = allItems;

        // 1. Category Filter
        if (selectedCategory !== 'All') {
            if (selectedCategory === 'Owned') {
                items = items.filter(i => (resources[i.id] || 0) > 0);
            } else {
                items = items.filter(i => i.category.toLowerCase() === selectedCategory.toLowerCase());
            }
        }

        // 2. Search Filter
        if (searchQuery.trim()) {
            const lowerQ = searchQuery.toLowerCase();
            items = items.filter(i =>
                i.name.toLowerCase().includes(lowerQ) ||
                i.subCategory?.toLowerCase().includes(lowerQ)
            );
        }

        // Sort: Owned first, then Name
        return items.sort((a, b) => {
            const ownedA = (resources[a.id] || 0) > 0 ? 1 : 0;
            const ownedB = (resources[b.id] || 0) > 0 ? 1 : 0;
            if (ownedA !== ownedB) return ownedB - ownedA; // Owned first
            return a.name.localeCompare(b.name);
        });

    }, [allItems, selectedCategory, searchQuery, resources]);

    // Categories
    const categories = ['All', 'Owned', 'Resource', 'Equipment', 'Farming', 'Other'];

    return (
        <div className="compendium-container" style={{
            // Removed fixed height to allow global scroll
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            minHeight: '100%'
        }}>
            {/* Toolbar */}
            <div className="compendium-toolbar" style={{
                display: 'flex',
                gap: '16px',
                padding: '0 0 16px 0',
                alignItems: 'center',
                flexWrap: 'wrap'
            }}>
                {/* Search */}
                <div style={{ position: 'relative', flex: '1 1 200px' }}>
                    <SearchInput
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={setSearchQuery}
                        options={useMemo(() => allItems.map(item => ({
                            value: item.id,
                            label: item.name,
                            icon: <ItemIcon id={item.id} size={20} />
                        })).sort((a, b) => a.label.localeCompare(b.label)), [allItems])}
                        onSelect={(id) => {
                            // When selecting from dropdown, we might want to also navigate to it?
                            // Or just filter? SearchInput default behavior filters by name.
                            // But since we have filteredItems based on searchQuery, 
                            // if I select "Wood", searchQuery becomes "Wood", and it filters to Wood.
                            // That seems correct. 
                            // Optionally, I could also auto-select it in the detail view?
                            handleNavigate(id as Resource);
                        }}
                    />
                </div>

                {/* Categories */}
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: 'none',
                                background: selectedCategory === cat ? 'var(--color-primary)' : 'rgba(255,255,255,0.05)',
                                color: selectedCategory === cat ? '#fff' : 'var(--color-text-secondary)',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                fontWeight: 500,
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Stats Bar */}
            <div style={{
                fontSize: '0.85rem',
                color: 'var(--color-text-muted)',
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <span>Showing {filteredItems.length} items</span>
                <span>
                    Collection Progress: {allItems.filter(i => (resources[i.id] || 0) > 0).length} / {allItems.length}
                </span>
            </div>

            {/* Main Content Area: Split View */}
            <div
                className="compendium-split-view"
                style={{
                    flex: 1,
                    display: 'flex',
                    gap: '16px',
                    // Removed overflow to allow global scroll
                    position: 'relative',
                    alignItems: 'flex-start' // Ensure items align to top
                }}>

                {/* Left: Grid */}
                <div className="compendium-main" style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0, // Flexbox nesting fix
                    background: 'rgba(0,0,0,0.1)',
                    borderRadius: '12px',
                    padding: '12px',
                    border: '1px solid var(--color-border)',
                }}>
                    {filteredItems.length > 0 ? (
                        <CompendiumGrid
                            items={filteredItems}
                            ownedCounts={resources}
                            onSelect={setSelectedItem}
                            scrollElement={scrollElement}
                            isDetailsOpen={!!selectedItem}
                        />
                    ) : (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: 'var(--color-text-muted)',
                            gap: '12px',
                            minHeight: '200px'
                        }}>
                            <BookOpen size={48} opacity={0.2} />
                            <p>No items found matching your criteria.</p>
                        </div>
                    )}
                </div>

                {/* Right: Details Panel */}
                {selectedItem && (
                    <div className="compendium-details-panel-wrapper" style={{
                        flex: '0 0 450px',
                        display: 'flex',
                        flexDirection: 'column',
                        // Sticky Positioning
                        position: 'sticky',
                        top: '0', // Stick to the top edge
                        alignSelf: 'flex-start',
                        maxHeight: 'calc(100vh - 96px)', // Constrain height within viewport (Header ~64px + Padding ~24px)
                        overflowY: 'auto', // Allow internal scrolling
                        borderRadius: '12px',
                        border: '1px solid var(--color-border)',
                        background: 'var(--color-bg-panel)',
                        zIndex: 10,
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}>
                        <ItemDetailView
                            item={selectedItem}
                            marketData={market[selectedItem.id]}
                            ownedCount={resources[selectedItem.id] || 0}
                            onClose={() => setSelectedItem(null)}
                            onNavigate={handleNavigate}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
